import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface HeroSectionProps {
  backgroundImage: string;
}

const useCountdown = (targetDate: Date): TimeLeft & { isOver: boolean } => {
  const calculate = (): TimeLeft & { isOver: boolean } => {
    const diff = targetDate.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, isOver: true };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      isOver: false,
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculate);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calculate()), 1000);
    return () => clearInterval(timer);
  }, []);

  return timeLeft;
};

const pad = (n: number) => String(n).padStart(2, '0');

const HeroSection = ({ backgroundImage }: HeroSectionProps) => {
  // April 25, 2026 at 2:00 PM Chicago CST (UTC-6 in April = CDT UTC-5, so 19:00 UTC)
  const eventDate = new Date('2026-05-30T12:30:00Z');
  const { days, hours, minutes, seconds, isOver } = useCountdown(eventDate);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt="AI Summit Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/60 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-16">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            {/* Organizer Badges */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-6">
              <span className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/80 text-xs font-medium whitespace-nowrap">
                Great Nigerian Assembly (GNA)
              </span>
              <span className="text-white/40 text-xs">×</span>
              <span className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/80 text-xs font-medium whitespace-nowrap">
                CPI Technologies Inc.
              </span>
            </div>

            {/* Edition Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-700/30 border border-green-500/40 backdrop-blur-sm mb-5">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              <span className="text-amber-300 text-sm font-semibold tracking-wide whitespace-nowrap">SME &amp; STARTUP EDITION</span>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4">
              GNA AI BUSINESS
              <span className="block bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
                SUMMIT 2026
              </span>
            </h1>

            {/* Theme */}
            <p className="text-lg md:text-xl text-white/75 font-medium mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Navigating the AI Revolution: Practical Strategies for Small Businesses and Startups
            </p>

            {/* Event Details Pills */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-8">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <i className="ri-calendar-line text-amber-400"></i>
                <span className="text-white text-sm font-semibold whitespace-nowrap">May 30th, 2026</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <i className="ri-global-line text-amber-400"></i>
                <span className="text-white text-sm font-semibold whitespace-nowrap">Live Online Event</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <i className="ri-time-line text-amber-400"></i>
                <span className="text-white text-sm font-semibold whitespace-nowrap">2 Hours</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-700/30 border border-green-500/40">
                <i className="ri-ticket-line text-green-300"></i>
                <span className="text-green-200 text-sm font-semibold whitespace-nowrap">FREE Registration</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10">
              <Link
                to="/register"
                className="w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-green-700 to-green-800 text-white font-bold text-base hover:opacity-90 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer whitespace-nowrap text-center"
              >
                Register Now — It's Free
              </Link>
              <Link
                to="/premium"
                className="w-full sm:w-auto px-8 py-4 rounded-full border-2 border-amber-400/60 text-amber-300 font-semibold text-base hover:bg-amber-400/10 transition-all duration-200 cursor-pointer whitespace-nowrap text-center"
              >
                Explore Premium – $100
              </Link>
            </div>

            {/* Countdown Timer */}
            {isOver ? (
              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-green-600/30 border border-green-400/40 backdrop-blur-sm">
                <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse shrink-0"></span>
                <span className="text-green-200 font-bold text-base tracking-wide">The Summit is LIVE now!</span>
              </div>
            ) : (
              <div className="w-full">
                <p className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-3 text-center lg:text-left">
                  Event starts in
                </p>
                <div className="flex items-stretch justify-center lg:justify-start gap-2 sm:gap-3">
                  {[
                    { value: days, label: 'Days' },
                    { value: hours, label: 'Hours' },
                    { value: minutes, label: 'Mins' },
                    { value: seconds, label: 'Secs' },
                  ].map((unit, idx) => (
                    <div key={unit.label} className="flex items-center gap-2 sm:gap-3">
                      <div className="flex flex-col items-center">
                        <div className="relative w-16 sm:w-20 h-16 sm:h-20 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-md border border-white/20 overflow-hidden">
                          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                          <span className="text-2xl sm:text-3xl font-extrabold text-white tabular-nums leading-none">
                            {pad(unit.value)}
                          </span>
                        </div>
                        <span className="text-white/50 text-[10px] sm:text-xs font-semibold uppercase tracking-widest mt-1.5">
                          {unit.label}
                        </span>
                      </div>
                      {idx < 3 && (
                        <span className="text-amber-400 font-bold text-xl sm:text-2xl mb-5 leading-none select-none">:</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Time Zone Card */}
          <div className="w-full lg:w-auto lg:min-w-[320px]">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-white">
              <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-4">Event Time — May 30th, 2026</p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/10">
                  <span className="text-2xl">🇺🇸</span>
                  <div>
                    <p className="text-xs text-white/60 font-medium">Chicago (CST)</p>
                    <p className="text-base font-bold">12:30 PM – 2:30 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/10">
                  <span className="text-2xl">🇳🇬</span>
                  <div>
                    <p className="text-xs text-white/60 font-medium">Nigeria (WAT)</p>
                    <p className="text-base font-bold">6:30 PM – 8:30 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/10">
                  <span className="text-2xl">🇬🇧</span>
                  <div>
                    <p className="text-xs text-white/60 font-medium">UK (BST)</p>
                    <p className="text-base font-bold">6:30 PM – 8:30 PM</p>
                  </div>
                </div>
              </div>
              <a
                href="https://www.timeanddate.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-2 text-amber-300 text-sm font-medium hover:text-amber-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                Convert to your local time
              </a>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/40 text-xs">Scroll to explore</span>
          <i className="ri-arrow-down-line text-white/40 text-lg"></i>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
